import React, { PureComponent } from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import { defineMessages, injectIntl, WrappedComponentProps } from 'react-intl';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Plus } from '@lifeomic/chromicons';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import getSurveyItemTestId from '../../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../../util/testProps';
import {
  Select,
  SelectOption as ChromaSelectOption,
  ComboBox,
  SelectOption,
} from '@lifeomic/chroma-react/components/Select';
import SelectValueUtil, {
  SupportedItemType,
  SupportedTypes,
  SupportedArrayTypes,
} from './SelectValueUtil';
import clsx from 'clsx';

export const testIds = {
  select: (linkId: string = '') => getSurveyItemTestId(linkId, 'select'),
  selectOption: (linkId: string = '', value: any) =>
    getSurveyItemTestId(linkId, `selectOption-${value}`),
  chip: (linkId: string = '', value: any) =>
    getSurveyItemTestId(linkId, `selectChip-${value}`),
  addOpenOption: (linkId: string = '') =>
    getSurveyItemTestId(linkId, 'addOpenOption'),
  noneOption: (linkId: string = '') =>
    getSurveyItemTestId(linkId, 'noneOption'),
};

const messages = defineMessages({
  addValue: {
    id: 'surveyBuilder.select.addValueButton.tooltip',
    defaultMessage: 'add custom value',
  },
  openChoiceIndicator: {
    id: 'surveyBuilder.select.openChoiceIndicator',
    defaultMessage: 'User Entered Value',
  },
  none: {
    id: 'surveyBuilder.select.none',
    defaultMessage: 'None',
  },
  addOpenChoice: {
    id: 'surveyBuilder.select.addOpenChoice',
    defaultMessage: 'Add Open Choice',
  },
  selectValues: {
    id: 'surveyBuilder.select.selectValues',
    defaultMessage: 'Select values',
  },
  selectAValue: {
    id: 'surveyBuilder.select.selectAValue',
    defaultMessage: 'Select a value',
  },
  surveySelect: {
    id: 'surveyBuilder.select.surveySelect',
    defaultMessage: 'survey select',
  },
});

const styles = (theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      display: 'flex',
      alignItems: 'flex-end',
      paddingLeft: theme.spacing(0.25),
    },
    formControl: {
      width: '100%',
      flex: 1,
    },
    addOpenEndedValueButton: {
      marginLeft: theme.spacing(2),
    },
    chipContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1),
    },
    chip: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    openChoiceMenuItem: {
      fontWeight: theme.typography.fontWeightMedium,
    },
    whiteBackground: {
      background: theme.palette.common.white,
      margin: 1,
    },
    labelRoot: {
      position: 'unset',
      lineHeight: 1.25,
    },
    noneOption: {
      fontStyle: 'italic',
    },
  });

interface SelectInputOwnProps {
  itemType: SupportedItemType;
  value: SupportedTypes | SupportedArrayTypes;
  onChange: (value: SupportedTypes | SupportedArrayTypes) => void;
  options: SupportedArrayTypes;
  isOpenChoice: boolean;
  isMultiSelect: boolean;
  label?: string;
  disabled?: boolean;
  error?: string;
  onAddOpenChoice: () => void;
  linkId?: string;
  className?: string;
}

type SelectInputProps = SelectInputOwnProps &
  WrappedComponentProps &
  WithStyles<typeof styles>;

class SelectInput extends PureComponent<SelectInputProps> {
  getValueUtil = (): SelectValueUtil =>
    new SelectValueUtil(
      {
        isMultiSelect: this.props.isMultiSelect,
        isOpenChoice: this.props.isOpenChoice,
        itemType: this.props.itemType,
      },
      this.props.options,
      this.props.value,
    );

  handleChangeMultiple = (selectedValues: string[] = []) => {
    const newMultiSelectValue =
      this.getValueUtil().processMultiSelectChange(selectedValues);
    this.props.onChange(newMultiSelectValue);
  };

  handleSingleSelectChange = (selectedValue: string) => {
    const newSingleSelectValue =
      this.getValueUtil().processSingleSelectChange(selectedValue);

    this.props.onChange(newSingleSelectValue);
  };

  getSelectOptions = () => {
    const openChoiceOptions = this.getValueUtil().openChoiceSelectOptions.map(
      selectOption => (
        <ChromaSelectOption
          key={`manual-${selectOption.displayValue.value}`}
          value={selectOption.displayValue.value}
          title={selectOption.displayValue.display}
          subtitle={this.props.intl.formatMessage(messages.openChoiceIndicator)}
          {...getTestProps(
            testIds.selectOption(
              this.props.linkId,
              selectOption.displayValue.value,
            ),
          )}
        />
      ),
    );

    const configuredOptions = this.getValueUtil().configuredSelectOptions.map(
      option => (
        <ChromaSelectOption
          key={option.displayValue.value}
          value={option.displayValue.value}
          title={option.displayValue.display}
          {...getTestProps(
            testIds.selectOption(this.props.linkId, option.displayValue.value),
          )}
        />
      ),
    );

    return [...openChoiceOptions, ...configuredOptions];
  };

  render() {
    const {
      classes,
      isMultiSelect,
      label,
      isOpenChoice,
      intl: { formatMessage },
      itemType,
      disabled,
      error,
      linkId,
      className,
    } = this.props;

    // coding inputs cannot be open choice
    const validatedIsOpenChoice =
      isOpenChoice &&
      itemType !== 'coding' &&
      itemType !== 'time' &&
      itemType !== 'date';

    return (
      <div className={clsx(classes.container, className)}>
        {isMultiSelect ? (
          <ComboBox
            className={className}
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            label={label}
            disabled={disabled}
            value={this.getValueUtil().multiSelectValue}
            onChange={this.handleChangeMultiple}
            hasError={!!error}
            errorMessage={error}
            popoverAriaLabel={formatMessage(messages.selectValues)}
            fullWidth
            {...getTestProps(testIds.select(linkId))}
          >
            {this.getSelectOptions()}
          </ComboBox>
        ) : (
          <Select
            className={className}
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            label={label}
            disabled={disabled}
            value={this.getValueUtil().selectValue}
            onChange={this.handleSingleSelectChange}
            hasError={!!error}
            errorMessage={error}
            popoverAriaLabel={formatMessage(messages.selectAValue)}
            fullWidth
            aria-label={label || formatMessage(messages.surveySelect)}
            {...getTestProps(testIds.select(linkId))}
          >
            {[
              <SelectOption
                key={'selectInput-noneOption'}
                value={''}
                title={formatMessage(messages.none)}
                className={classes.noneOption}
                {...getTestProps(testIds.noneOption(linkId))}
              />,
              ...this.getSelectOptions(),
            ].filter(Boolean)}
          </Select>
        )}
        {!validatedIsOpenChoice ? null : (
          <div className={classes.addOpenEndedValueButton}>
            <Tooltip title={formatMessage(messages.addValue)}>
              <IconButton
                aria-label={formatMessage(messages.addOpenChoice)}
                size={0}
                icon={Plus}
                onClick={this.props.onAddOpenChoice}
                {...getTestProps(testIds.addOpenOption(linkId))}
              />
            </Tooltip>
          </div>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(injectIntl(SelectInput));
